import { Box, styled } from "@mui/material";
import { RenderStateCamera } from "@novorender/api";
import { Blueprint, Home } from "@procore/core-icons";
import { Button } from "@procore/core-react";
import { vec2 } from "gl-matrix";
import { useCallback, useEffect, useRef, useState } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { MINIMAP_LAYOUT } from "components/toolbars/constants";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { useResetView } from "features/home/useResetView";
import { telemetry } from "telemetry";
import { AsyncStatus } from "types/misc";

import { LargeMapMobile } from "./largeMapMobile";
import {
    selectCurrentDrawing,
    selectIsLargeMapMobileOpen,
    selectMiniMapDrawings,
    selectMiniMapLevels,
    setCurrentDrawing,
    setDrawingData,
    setLargeMapMobileOpen,
} from "./minimapSlice";
import { drawPositionIndicator, fetchDrawingData, findDrawingIdByElevation, modelToDrawingSpace } from "./miniMapUtils";
import { DrawingSpacePosition } from "./types";
import { useMiniMapData } from "./useMiniMapData";

const MiniMapContainer = styled(Box)({
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    padding: "8px",
    borderRadius: 8,
    background: "linear-gradient(179.92deg, rgba(244, 240, 240, 0.5) 97.81%, rgba(197, 197, 197, 0.5) 118%)",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.08)",
    pointerEvents: "auto",
    width: "min-content",
});

const StyledCanvas = styled("canvas")({
    display: "block",
    borderRadius: 10,
    boxShadow: "0 4px 4px 0 rgba(0, 0, 0, 0.08), 0 8px 16px -6px rgba(23, 26, 28, 0.24)",
    backgroundColor: "white",
    cursor: "pointer",
});

const CANVAS_WIDTH = 92;
const CANVAS_HEIGHT = 92;
const MAP_SCALE = 0.2;

function MiniMap({ onClick }: { onClick?: () => void }) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const currentDrawing = useAppSelector(selectCurrentDrawing);
    const levels = useAppSelector(selectMiniMapLevels);
    const drawings = useAppSelector(selectMiniMapDrawings);
    const dispatch = useAppDispatch();
    const {
        state: { view, internalEventEmitter },
    } = useExplorerGlobals();

    const [drawingPosition, setDrawingPosition] = useState<DrawingSpacePosition>({
        position: vec2.fromValues(0.5, 0.5),
        rotation: 0,
    });

    const drawMap = useCallback(
        (ctx: CanvasRenderingContext2D, img: ImageBitmap, drawingPosition: DrawingSpacePosition) => {
            ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

            const scaledWidth = img.width * MAP_SCALE;
            const scaledHeight = img.height * MAP_SCALE;

            const posXPercent = drawingPosition.position[0];
            const posYPercent = drawingPosition.position[1];

            const posXOnScaledImage = posXPercent * scaledWidth;
            const posYOnScaledImage = posYPercent * scaledHeight;

            const centerX = CANVAS_WIDTH / 2;
            const centerY = CANVAS_HEIGHT / 2;

            const offsetX = centerX - posXOnScaledImage;
            const offsetY = centerY - posYOnScaledImage;

            ctx.drawImage(img, offsetX, offsetY, scaledWidth, scaledHeight);

            drawPositionIndicator(ctx, centerX, centerY, drawingPosition.rotation);
        },
        [],
    );

    useEffect(() => {
        if (!view || !internalEventEmitter || levels.length === 0) {
            return;
        }

        const handleCameraUpdated = (camera: RenderStateCamera) => {
            const cameraZ = camera.position[2];

            const targetDrawingId = findDrawingIdByElevation(cameraZ, levels, currentDrawing?.id);
            if (targetDrawingId && targetDrawingId !== currentDrawing?.id) {
                dispatch(setCurrentDrawing(targetDrawingId));

                // Fetch the drawing data for the new drawing if we don't have it already
                const targetDrawing = drawings[targetDrawingId];
                if (targetDrawing) {
                    const { status } = targetDrawing.drawingData.asyncState;
                    if (status === AsyncStatus.Initial) {
                        dispatch(
                            setDrawingData({
                                drawingId: targetDrawing.id,
                                asyncState: { status: AsyncStatus.Loading },
                            }),
                        );
                        fetchDrawingData(targetDrawing.drawingData.url)
                            .then((imgBitmap) => {
                                dispatch(
                                    setDrawingData({
                                        drawingId: targetDrawing.id,
                                        asyncState: { status: AsyncStatus.Success, data: imgBitmap },
                                    }),
                                );
                            })
                            .catch((error) => {
                                const e = error as Error;
                                telemetry.trackCaughtException({
                                    source: "miniMap.miniMapMobile.fetchDrawingData",
                                    category: "render",
                                    error,
                                    extraAttributes: { drawingId: targetDrawing.id },
                                });
                                dispatch(
                                    setDrawingData({
                                        drawingId: targetDrawing.id,
                                        asyncState: { status: AsyncStatus.Error, msg: e.message },
                                    }),
                                );
                            });
                    }
                }
            }

            if (currentDrawing?.modelAB && currentDrawing?.drawingAB) {
                const result = modelToDrawingSpace(currentDrawing.modelAB, currentDrawing.drawingAB, camera);
                setDrawingPosition(result);
            }
        };

        handleCameraUpdated(view.renderState.camera);

        const unsubscribe = internalEventEmitter.on("cameraUpdated", handleCameraUpdated);

        return () => {
            unsubscribe();
        };
    }, [view, internalEventEmitter, levels, currentDrawing, dispatch, drawings]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }

        const ctx = canvas.getContext("2d");
        if (!ctx) {
            return;
        }

        const dpr = window.devicePixelRatio || 1;

        canvas.width = CANVAS_WIDTH * dpr;
        canvas.height = CANVAS_HEIGHT * dpr;
        canvas.style.width = `${CANVAS_WIDTH}px`;
        canvas.style.height = `${CANVAS_HEIGHT}px`;
        ctx.scale(dpr, dpr);

        if (!currentDrawing) {
            drawBackground(ctx, CANVAS_WIDTH, CANVAS_HEIGHT);
            return;
        }

        const { status } = currentDrawing.drawingData.asyncState;
        switch (status) {
            case AsyncStatus.Initial:
            case AsyncStatus.Loading:
                drawBackground(ctx, CANVAS_WIDTH, CANVAS_HEIGHT);
                break;
            case AsyncStatus.Success:
                drawMap(ctx, currentDrawing.drawingData.asyncState.data, drawingPosition);
                break;
            case AsyncStatus.Error:
            default:
                drawBackground(ctx, CANVAS_WIDTH, CANVAS_HEIGHT);
        }
    }, [currentDrawing, drawingPosition, drawMap]);

    return <StyledCanvas ref={canvasRef} onClick={onClick} />;
}

function drawBackground(ctx: CanvasRenderingContext2D, width: number, height: number) {
    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, "#a0a0a0");
    gradient.addColorStop(1, "#d0d0d0");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
}

export function MiniMapMobile() {
    const resetView = useResetView();
    const dispatch = useAppDispatch();
    const currentDrawing = useAppSelector(selectCurrentDrawing);
    const isLargeMapOpen = useAppSelector(selectIsLargeMapMobileOpen);
    const [isCompactMode, setIsCompactMode] = useState(false);

    useMiniMapData();

    useEffect(() => {
        const checkHeight = () => {
            setIsCompactMode(window.innerHeight < MINIMAP_LAYOUT.MINIMAP_MIN_HEIGHT);
        };

        checkHeight();
        window.addEventListener("resize", checkHeight);
        return () => window.removeEventListener("resize", checkHeight);
    }, []);

    const onHomeClick = async () => {
        await resetView();
    };

    const handleMiniMapClick = () => {
        dispatch(setLargeMapMobileOpen(true));
    };

    return (
        <>
            <MiniMapContainer>
                {isCompactMode
                    ? currentDrawing && (
                          <Button
                              variant="secondary"
                              icon={<Blueprint />}
                              onClick={handleMiniMapClick}
                              style={{ backgroundColor: "white" }}
                          />
                      )
                    : currentDrawing && <MiniMap onClick={handleMiniMapClick} />}

                <Button
                    variant="secondary"
                    icon={<Home />}
                    onClick={onHomeClick}
                    style={{ backgroundColor: "white" }}
                />
            </MiniMapContainer>

            <LargeMapMobile open={isLargeMapOpen} />
        </>
    );
}
